import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Item } from '../../../../components/Grid';
import HistoryRowControls from '../../../../components/HistoryRowControls';
import HistoryRowListing from '../../../../components/HistoryRowListing';
import HistorySection from '../../../../components/HistorySection';
import DocumentCreationButton from '../../../../components/DocumentCreationButton';
import { formatPartialDate } from 'Utility/date';
import {
  calculateCeradTotal,
  getCeradAbnormalTasks,
  getCeradEducationYears,
  getCeradTotalCutOffScore,
  getCeradUpdatedComputedData,
} from 'Routes/Cognition/utils/cerad';
import { maxScores } from '../Form/CERAD/config';
import DescriptionText from 'Routes/Cognition/utils/DescriptionText';

const CERAD = ({ documents, startEdit, view }: IOwnProps): React.JSX.Element => {
  return (
    <HistorySection
      headerText={<FormattedMessage id="cognition.cerad.ceradLong" />}
      newButton={
        <DocumentCreationButton
          name="cerad"
          text="cognition.cerad.newCerad"
          // Implement selection of different version if/when needed
          onClick={startEdit({}, 'cerad', { version: '2023' })}
          width={15}
          height={3}
          fontSize={14}
          alternate={true}
        />
      }
      headerInfoText={<DescriptionText type={'cerad'} />}
    >
      <HistoryRowListing
        documents={documents.filter(d => d._type === 'cerad')}
        headers={
          <>
            <Item xs={2}>
              <FormattedMessage id="general.date" />
            </Item>
            <Item xs={2}>
              <FormattedMessage id="cognition.cerad.total" />
            </Item>
            <Item xs={5}>
              <FormattedMessage id="cognition.cerad.abnormalTasksCount" />
            </Item>
          </>
        }
        contentFormat={(document: Partial<ICERAD2023>): React.JSX.Element => {
          const d = document as ICERAD2023;

          const bgDocs: IBackground[] = documents?.filter(doc => doc._type === 'background');

          const abnormalTasks = getCeradAbnormalTasks(d, getCeradUpdatedComputedData(d, bgDocs));

          const totalScore = calculateCeradTotal(d);
          const totalCutOffScore = getCeradTotalCutOffScore(getCeradEducationYears(d, bgDocs));

          return (
            <>
              <Item xs={2}>{d.date ? formatPartialDate(d.date) : '-'}</Item>
              <Item xs={2}>
                {typeof totalScore === 'number' ? (
                  <span>
                    {totalScore}
                    <span style={{ fontWeight: 400 }}>{` / ${maxScores.total}`}</span>
                    {typeof totalCutOffScore === 'number' && (
                      <span style={{ whiteSpace: 'nowrap' }}>
                        {' ('}
                        <FormattedMessage id={`cognition.cerad.${totalScore < totalCutOffScore ? 'ab' : ''}normal`} />
                        {')'}
                      </span>
                    )}
                  </span>
                ) : (
                  '-'
                )}
              </Item>
              <Item xs={5}>{typeof totalScore === 'number' ? abnormalTasks.length : '-'}</Item>
              <Item xs={3}>
                <HistoryRowControls document={d} startEdit={startEdit} view={view} />
              </Item>
            </>
          );
        }}
      />
    </HistorySection>
  );
};

interface IOwnProps {
  documents: (ICERAD | IBackground)[];
  startEdit: (
    document: TAnyObject,
    name?: string,
    initialFormData?: Record<string, any>,
  ) => (e: React.MouseEvent<Element, MouseEvent>) => void;
  view?: IView;
}

export default CERAD;
